import { useCallback, useContext, useState } from 'react';

import { useRequestWhatsAppPairingCodeMutation } from '@app/api/SocialIntegrations';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';

interface UseWhatsAppPairingCodeParams {
  integrationId: number | null;
  activeBranchId: number | null;
}

/**
 * Запасной вход WhatsApp по коду сопряжения (us/10721, task/10855):
 * номер телефона → RequestWhatsAppPairingCode → код, который пользователь
 * вводит на телефоне вместо сканирования QR. Успех входа приходит тем же
 * путём, что и у QR (SignalR WhatsAppSenderQR), — отдельного поллинга нет.
 */
export const useWhatsAppPairingCode = ({
  integrationId,
  activeBranchId,
}: UseWhatsAppPairingCodeParams) => {
  const [requestPairingCode, { isLoading: isCodeLoading }] =
    useRequestWhatsAppPairingCodeMutation();

  const [pairingCode, setPairingCode] = useState<string | null>(null);
  const [phone, setPhone] = useState<string | null>(null);

  const { showNotification } = useContext(NotificationContext);

  const submitPhone = useCallback(
    async (rawPhone: string) => {
      if (!integrationId || !activeBranchId || !rawPhone) return;

      try {
        const result = await requestPairingCode({
          ActiveBranchId: activeBranchId,
          integrationId,
          data: { Phone: rawPhone },
        }).unwrap();

        setPhone(rawPhone);
        setPairingCode(result.Code);
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    },
    [integrationId, activeBranchId, requestPairingCode, showNotification]
  );

  const resetPairing = useCallback(() => {
    setPairingCode(null);
    setPhone(null);
  }, []);

  return { pairingCode, phone, isCodeLoading, submitPhone, resetPairing };
};
